// Phase compiler-undo-stack (TODO-057)
//
// 20-entry client-side snapshot ring buffer for the Compiler page.
// Each entry stores a human-readable description and an async `undo`
// callback that dispatches the inverse mutation through the existing REST APIs.
//
// Pop is synchronous (via useRef) so the Ctrl+Z handler can immediately
// invoke `entry.undo()` without waiting for a React state flush.

import { useCallback, useRef, useState } from 'react';

export interface UndoEntry {
  /** Human-readable description, e.g. "Move 'study-report.pdf' back to m3/3.2.s.1". */
  description: string;
  /** Inverse mutation — dispatches through the existing REST APIs. */
  undo: () => Promise<void>;
}

const MAX_UNDO = 20;

export function useUndoStack() {
  // Keep both a React state (drives re-renders for canUndo / undoLabel)
  // and a ref (provides synchronous pop() without waiting for reconciliation).
  const [stack, setStack] = useState<UndoEntry[]>([]);
  const stackRef = useRef<UndoEntry[]>([]);

  const push = useCallback((entry: UndoEntry) => {
    const next = [...stackRef.current.slice(-(MAX_UNDO - 1)), entry];
    stackRef.current = next;
    setStack(next);
  }, []);

  const pop = useCallback((): UndoEntry | undefined => {
    if (stackRef.current.length === 0) return undefined;
    const popped = stackRef.current[stackRef.current.length - 1];
    const next = stackRef.current.slice(0, -1);
    stackRef.current = next;
    setStack(next);
    return popped;
  }, []);

  const clear = useCallback(() => {
    stackRef.current = [];
    setStack([]);
  }, []);

  const canUndo = stack.length > 0;
  const undoLabel = stack[stack.length - 1]?.description;

  return { push, pop, clear, canUndo, undoLabel };
}
